import { useGlobal } from '@hooks/useGlobal';
import { OpensiteRoutes } from '@router/openSiteRoute';
import { ROLE } from '@router/role';
import { RoleRoute } from '@router/roleRoute';
import { appRoutes } from '@router/routes';
import { ConfigProvider } from 'antd';
import enUs from 'antd/es/locale/en_US';
import jaJp from 'antd/es/locale/ja_JP';
import { lazy, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import { IndexRoute } from './router/indexRoute';

import { PageProvider } from '@/context/Page/PageProvider';
import { PrivateRoute } from '@/router/privateRoute';

import './App.less';
import './i18n';

const AuthLayout = lazy(() => import('@/layout/Auth'));
const RemoteSetting = lazy(() => import('@/pages/Remote/Setting'));
const RemoteDashboard = lazy(() => import('@/pages/Remote/Dashboard'));
const FeesClassifyRegister = lazy(() => import('@/pages/FeesClassifyRegister'));
const CustomerTypeRegister = lazy(() => import('@/pages/CustomerTypeRegister'));
const PageLayout = lazy(() => import('@/layout/Main'));
const Login = lazy(() => import('@/pages/Login'));
const DeleteAccount = lazy(() => import('@/pages/DeleteAccount'));
const Revenue = lazy(() => import('@/pages/Revenue'));
const DiscountReport = lazy(() => import('@/pages/DiscountReport'));
const CardReport = lazy(() => import('@/pages/CardReport'));
const Customer = lazy(() => import('@/pages/Customer'));
const UnregisteredCustomers = lazy(() => import('@/pages/UnregisteredCustomers'));
const CustomerDetailRegister = lazy(() => import('@/pages/CustomerDetailRegister'));
const CustomerDetailUpdate = lazy(() => import('@/pages/CustomerDetailUpdate'));
const ProductDailyReport = lazy(() => import('@/pages/ProductReport/DailyReport'));
const BulletinDailyReport = lazy(() => import('@/pages/StoreReport/BulletinOfSBS'));
const ProductMonthlyReport = lazy(() => import('@/pages/ProductReport/MonthlyReport'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const ServerError = lazy(() => import('@/pages/ServerError'));
const StatisticsDailyReport = lazy(() => import('@/pages/DailyReport'));
const ProductList = lazy(() => import('@/pages/Register/Product/List'));
const ProductPriceList = lazy(() => import('@/pages/Register/ProductPrice/List'));
const AddProduct = lazy(() => import('@/pages/Register/Product/AddProduct'));
const ProductUpdatePrice = lazy(() => import('@/pages/Register/ProductPrice/UpdatePrice'));
const ProductPriceSortOrder = lazy(() => import('@/pages/Register/ProductPrice/SortOrder'));
const TotalOfAllStore = lazy(() => import('@/pages/TotalOfAllStore'));
const CategoryList = lazy(() => import('@/pages/Register/Category/List'));
const CategoryRegister = lazy(() => import('@/pages/Register/Category/AddCategory'));
const CategorySortOrder = lazy(() => import('@/pages/Register/Category/SortOrder'));

// const SeatListClose = lazy(() => import('@/pages/SeatListClose'));
const ListCloseRoom = lazy(() => import('@/pages/CloseRoom/List'));
const AddCloseRoom = lazy(() => import('@/pages/CloseRoom/Add'));
// const SeatRegisterClose = lazy(() => import('@/pages/SeatRegisterClose'));
// const SeatListType = lazy(() => import('@/pages/SeatListType'));
// const SeatRegisterType = lazy(() => import('@/pages/SeatRegisterType'));
const SeatReportMonthly = lazy(() => import('@/pages/SeatReportMonthly'));
const SeatUsageHistory = lazy(() => import('@/pages/SeatUsageHistory'));
const Room = lazy(() => import('@/pages/Room'));
const RoomUsage = lazy(() => import('@/pages/RoomUsage'));

const ListFloor = lazy(() => import('@/pages/Floors/ListFloor'));
const AddFloor = lazy(() => import('@/pages/Floors/AddFloor'));

const ListStaff = lazy(() => import('@/pages/Staff/ListStaff'));
const RegisterStaff = lazy(() => import('@/pages/Staff/RegisterStaff'));

const ListDiscount = lazy(() => import('@/pages/Discount/ListDiscount'));
const RegisterDiscount = lazy(() => import('@/pages/Discount/RegisterDiscount'));

const ListPlan = lazy(() => import('@/pages/Plan/ListPlan'));
const PlanRegister = lazy(() => import('@/pages/Plan/PlanRegister'));

const ListStoreAccess = lazy(() => import('@/pages/StoreAccess/ListStoreAccess'));
const StoreAccessRegister = lazy(() => import('@/pages/StoreAccess/StoreAccessRegister'));

const ListCard = lazy(() => import('@/pages/Card/ListCard'));
const RegisterCard = lazy(() => import('@/pages/Card/RegisterCard'));

const SettingStore = lazy(() => import('@/pages/SettingStore'));
const SettingBanner = lazy(() => import('@/pages/Banner'));
const PrintSetting = lazy(() => import('@/pages/PrintSetting'));
const ListPrinter = lazy(() => import('@/pages/Printer/ListPrinter'));
const RegisterPrinter = lazy(() => import('@/pages/Printer/RegisterPrinter'));
const AuthenticateSetting = lazy(() => import('@/pages/AuthenticateSetting'));

const ListUser = lazy(() => import('@/pages/User/ListUser'));
const AddEditUser = lazy(() => import('@/pages/User/RegisterUser'));

const RegisterReceipt = lazy(() => import('@/pages/Receipt'));

const EditHistoryByMonth = lazy(() => import('@/pages/EditHistoryByMonth'));
const SalesEditHistory = lazy(() => import('@/pages/SalesEditHistory'));
const SlipEditHistory = lazy(() => import('@/pages/SlipEditHistory'));
const SlipEditHistoryDetail = lazy(() => import('@/pages/SlipEditHistoryDetail'));
const PriceEditHistory = lazy(() => import('@/pages/PriceEditHistory'));
const CurrentExchange = lazy(() => import('@/pages/CurrentExchange'));
const StockHistory = lazy(() => import('@/pages/StockHistory'));

const EquipmentRegister = lazy(() => import('@/pages/EquipmentRegister'));

const BusinessDayRegister = lazy(() => import('@/pages/BusinessDayRegister'));

const ListRoomType = lazy(() => import('@/pages/RoomType/List'));
const AddRoomType = lazy(() => import('@/pages/RoomType/Add'));

const Profile = lazy(() => import('@/pages/Profile'));
const ProfileEdit = lazy(() => import('@/pages/Profile/Edit'));

const ListWorkingSession = lazy(() => import('@/pages/WorkManage/List'));
const AddWorkingSession = lazy(() => import('@/pages/WorkManage/Add'));
const UpdateWorkingSession = lazy(() => import('@/pages/WorkManage/Update'));
const WorkManageReview = lazy(() => import('@/pages/WorkManage/Review'));

const Coupon = lazy(() => import('@/pages/Coupon'));
const CustomerMobile = lazy(() => import('@/pages/CustomerMobile'));
const AppHistory = lazy(() => import('@/pages/AppHistory'));
const ListTicket = lazy(() => import('@/pages/Ticket/ListTicket'));
const AddTicket = lazy(() => import('@/pages/Ticket/AddTicket'));
const IssueTicket = lazy(() => import('@/pages/Ticket/IssueTicket'));
const ListIssuedTicket = lazy(() => import('@/pages/Ticket/ListIssuedTicket'));
const UsedTicketHistory = lazy(() => import('@/pages/Ticket/UsedTicketHistory'));
const HistoryIssuedStamps = lazy(() => import('@/pages/IssuedStampsHistory'));
const MessagePage = lazy(() => import('@/pages/Message'));
const SmartPhoneCustomerList = lazy(() => import('@/pages/SmartPhoneCustomer/List'));
const CustomerSmartPhoneDetail = lazy(() => import('@/pages/SmartPhoneCustomer/Detail'));
const UseTicket = lazy(() => import('@/pages/Ticket/UsedTicked'));
const OutTimeBundle = lazy(() => import('@/pages/OutTimeBundle'));
const AppDownloads = lazy(() => import('@/pages/AppDownloads'));

type LazyLoadHOC = {
    component: React.LazyExoticComponent<any>;
};

export const LazyLoadComponent: React.FC<LazyLoadHOC> = ({ component: Component, ...props }) => {
    return (
        <PageProvider>
            <Component {...props} />
        </PageProvider>
    );
};

function App() {
    const { isLoggedIn: isAuthenticated } = useGlobal();
    const { i18n, t } = useTranslation();
    const [locale, setLocal] = useState(jaJp);
    const lang = i18n.language;

    useEffect(() => {
        switch (lang) {
            case 'ja':
                setLocal(jaJp);
                break;
            case 'en':
                setLocal(enUs);
                break;
            default:
                setLocal(jaJp);
                break;
        }
    }, [i18n.changeLanguage, lang]);

    return (
        <ConfigProvider locale={locale}>
            <BrowserRouter>
                <Helmet>
                    <title>{t('common:APP_TITLE')}</title>
                </Helmet>
                <PageProvider>
                    <Routes>
                        <Route element={<OpensiteRoutes isAuthenticated={isAuthenticated} />}>
                            <Route element={<AuthLayout />}>
                                <Route path={`${appRoutes.AUTH.LOGIN}`} element={<Login />} />
                                <Route path={`${appRoutes.AUTH.DELETE_ACCOUNT}`} element={<DeleteAccount />} />
                            </Route>
                        </Route>
                        <Route element={<PrivateRoute isAuthenticated={isAuthenticated} />}>
                            <Route element={<PageLayout />}>
                                <Route element={<RoleRoute roles={[ROLE.HEAD]} />}>
                                    <Route index element={<IndexRoute />} />
                                    <Route
                                        path={`${appRoutes.SALES_MANAGEMENT.REVENUE}`}
                                        element={<LazyLoadComponent component={Revenue} />}
                                    />
                                    <Route
                                        path={`${appRoutes.SALES_MANAGEMENT.DISCOUNT}`}
                                        element={<LazyLoadComponent component={DiscountReport} />}
                                    />
                                    <Route
                                        path={`${appRoutes.SALES_MANAGEMENT.CARD}`}
                                        element={<LazyLoadComponent component={CardReport} />}
                                    />
                                    <Route
                                        path={`${appRoutes.CUSTOMER.LIST}`}
                                        element={<LazyLoadComponent component={Customer} />}
                                    />
                                    <Route
                                        path={`${appRoutes.CUSTOMER.UNREGISTERED_CUSTOMERS}`}
                                        element={<LazyLoadComponent component={UnregisteredCustomers} />}
                                    />
                                    <Route
                                        path={`${appRoutes.CUSTOMER.UNAUTHORIZED_SMARTPHONE_CUSTOMER}`}
                                        element={<LazyLoadComponent component={SmartPhoneCustomerList} />}
                                    />
                                    <Route
                                        path={`${appRoutes.CUSTOMER.SMARTPHONE_CUSTOMER_DETAIL}`}
                                        element={<LazyLoadComponent component={CustomerSmartPhoneDetail} />}
                                    />
                                    <Route
                                        path={`${appRoutes.CUSTOMER.DETAIL_REGISTER}`}
                                        element={<LazyLoadComponent component={CustomerDetailRegister} />}
                                    />
                                    <Route
                                        path={`${appRoutes.PRICE_SEAT_MANAGEMENT.DEVICE.CUSTOMER_TYPE_REGISTER}`}
                                        element={<LazyLoadComponent component={CustomerTypeRegister} />}
                                    />
                                    <Route
                                        path={`${appRoutes.CUSTOMER.DETAIL_UPDATE}`}
                                        element={<LazyLoadComponent component={CustomerDetailUpdate} />}
                                    />
                                    <Route
                                        path={`${appRoutes.SALES_MANAGEMENT.DAILY_REPORT}`}
                                        element={<LazyLoadComponent component={StatisticsDailyReport} />}
                                    />
                                    <Route
                                        path={`${appRoutes.PRICE_SEAT_MANAGEMENT.FEES_CLASSIFY_REGISTER}`}
                                        element={<LazyLoadComponent component={FeesClassifyRegister} />}
                                    />

                                    <Route
                                        path={`${appRoutes.SALES_MANAGEMENT.PRODUCT_REPORT.DAILY_REPORT}`}
                                        element={<LazyLoadComponent component={ProductDailyReport} />}
                                    />
                                    <Route
                                        path={`${appRoutes.SALES_MANAGEMENT.TOTAL_OF_ALL_STORE}`}
                                        element={<LazyLoadComponent component={TotalOfAllStore} />}
                                    />
                                    <Route
                                        path={`${appRoutes.SALES_MANAGEMENT.PRODUCT_REPORT.MONTHLY_REPORT}`}
                                        element={<LazyLoadComponent component={ProductMonthlyReport} />}
                                    />

                                    <Route
                                        path={`${appRoutes.MASTER_MANAGEMENT.STAFF.ROOT}`}
                                        element={<LazyLoadComponent component={ListStaff} />}
                                    />
                                    <Route
                                        path={`${appRoutes.MASTER_MANAGEMENT.STAFF.REGISTER}`}
                                        element={<LazyLoadComponent component={RegisterStaff} />}
                                    />
                                    <Route
                                        path={`${appRoutes.MASTER_MANAGEMENT.STAFF.UPDATE}/:id`}
                                        element={<LazyLoadComponent component={RegisterStaff} />}
                                    />
                                    <Route
                                        path={`${appRoutes.SALES_MANAGEMENT.PRODUCT_REPORT.MONTHLY_REPORT}`}
                                        element={<LazyLoadComponent component={SeatReportMonthly} />}
                                    />
                                    <Route
                                        path={`${appRoutes.PRICE_SEAT_MANAGEMENT.SEAT.LIST_CLOSE}`}
                                        element={<LazyLoadComponent component={ListCloseRoom} />}
                                    />
                                    <Route
                                        path={`${appRoutes.PRICE_SEAT_MANAGEMENT.SEAT.REGISTER_CLOSE}`}
                                        element={<LazyLoadComponent component={AddCloseRoom} />}
                                    />
                                    <Route
                                        path={`${appRoutes.PRICE_SEAT_MANAGEMENT.SEAT.UPDATE_CLOSE}/:id`}
                                        element={<LazyLoadComponent component={AddCloseRoom} />}
                                    />
                                    <Route
                                        path={`${appRoutes.PRICE_SEAT_MANAGEMENT.SEAT.LIST_TYPE}`}
                                        element={<LazyLoadComponent component={ListRoomType} />}
                                    />
                                    <Route
                                        path={`${appRoutes.PRICE_SEAT_MANAGEMENT.SEAT.REGISTER_TYPE}`}
                                        element={<LazyLoadComponent component={AddRoomType} />}
                                    />
                                    <Route
                                        path={`${appRoutes.PRICE_SEAT_MANAGEMENT.SEAT.UPDATE_TYPE}/:id`}
                                        element={<LazyLoadComponent component={AddRoomType} />}
                                    />
                                    <Route
                                        path={`${appRoutes.SALES_MANAGEMENT.STORE_REPORT.LIST_OF_SALES}`}
                                        element={<LazyLoadComponent component={BulletinDailyReport} />}
                                    />
                                    {/* <Route
                                    path={`${appRoutes.SALES_MANAGEMENT.STORE_REPORT.BULLETIN_OF_SBS}`}
                                    element={<LazyLoadComponent component={BulletinDailyReport} />}
                                /> */}
                                    <Route
                                        path={`${appRoutes.PRICE_SEAT_MANAGEMENT.PRODUCT.PRODUCT_LIST}`}
                                        element={<LazyLoadComponent component={ProductList} />}
                                    />
                                    <Route
                                        path={`${appRoutes.PRICE_SEAT_MANAGEMENT.PRODUCT.ADD_PRODUCT}`}
                                        element={<LazyLoadComponent component={AddProduct} />}
                                    />
                                    <Route
                                        path={`${appRoutes.PRICE_SEAT_MANAGEMENT.PRODUCT.ADD_PRODUCT}/:id`}
                                        element={<LazyLoadComponent component={AddProduct} />}
                                    />
                                    <Route
                                        path={`${appRoutes.PRICE_SEAT_MANAGEMENT.PRODUCT_PRICE.LIST}`}
                                        element={<LazyLoadComponent component={ProductPriceList} />}
                                    />
                                    <Route
                                        path={`${appRoutes.PRICE_SEAT_MANAGEMENT.PRODUCT_PRICE.UPDATE_PRICE}/:id`}
                                        element={<LazyLoadComponent component={ProductUpdatePrice} />}
                                    />
                                    <Route
                                        path={`${appRoutes.PRICE_SEAT_MANAGEMENT.PRODUCT_PRICE.SORT_ORDER}`}
                                        element={<LazyLoadComponent component={ProductPriceSortOrder} />}
                                    />
                                    <Route
                                        path={`${appRoutes.PRICE_SEAT_MANAGEMENT.CATEGORY.LIST}`}
                                        element={<LazyLoadComponent component={CategoryList} />}
                                    />
                                    <Route
                                        path={`${appRoutes.PRICE_SEAT_MANAGEMENT.CATEGORY.REGISTER}`}
                                        element={<LazyLoadComponent component={CategoryRegister} />}
                                    />
                                    <Route
                                        path={`${appRoutes.PRICE_SEAT_MANAGEMENT.CATEGORY.UPDATE}/:id`}
                                        element={<LazyLoadComponent component={CategoryRegister} />}
                                    />
                                    <Route
                                        path={`${appRoutes.PRICE_SEAT_MANAGEMENT.CATEGORY.SORT_ORDER}`}
                                        element={<LazyLoadComponent component={CategorySortOrder} />}
                                    />
                                    <Route
                                        path={`${appRoutes.SALES_MANAGEMENT.SEAT_REPORT.MONTHLY_REPORT}`}
                                        element={<LazyLoadComponent component={SeatReportMonthly} />}
                                    />
                                    <Route
                                        path={`${appRoutes.SALES_MANAGEMENT.SEAT_USAGE_HISTORY.ROOT}`}
                                        element={<LazyLoadComponent component={SeatUsageHistory} />}
                                    />
                                    <Route
                                        path={`${appRoutes.PRICE_SEAT_MANAGEMENT.ROOM.ROOT}`}
                                        element={<LazyLoadComponent component={Room} />}
                                    />
                                    <Route
                                        path={`${appRoutes.SALES_MANAGEMENT.SEAT_USAGE.ROOT}`}
                                        element={<LazyLoadComponent component={RoomUsage} />}
                                    />
                                    <Route
                                        path={`${appRoutes.MASTER_MANAGEMENT.DISCOUNT.ROOT}`}
                                        element={<LazyLoadComponent component={ListDiscount} />}
                                    />
                                    <Route
                                        path={`${appRoutes.MASTER_MANAGEMENT.DISCOUNT.REGISTER}`}
                                        element={<LazyLoadComponent component={RegisterDiscount} />}
                                    />
                                    <Route
                                        path={`${appRoutes.MASTER_MANAGEMENT.DISCOUNT.UPDATE}/:id`}
                                        element={<LazyLoadComponent component={RegisterDiscount} />}
                                    />
                                    <Route
                                        path={`${appRoutes.PRICE_SEAT_MANAGEMENT.PLAN.ROOT}`}
                                        element={<LazyLoadComponent component={ListPlan} />}
                                    />
                                    <Route
                                        path={`${appRoutes.PRICE_SEAT_MANAGEMENT.PLAN.LIST}`}
                                        element={<LazyLoadComponent component={ListPlan} />}
                                    />
                                    <Route
                                        path={`${appRoutes.PRICE_SEAT_MANAGEMENT.PLAN.REGISTER}`}
                                        element={<LazyLoadComponent component={PlanRegister} />}
                                    />

                                    <Route
                                        path={`${appRoutes.PRICE_SEAT_MANAGEMENT.PLAN.REGISTER}/:id`}
                                        element={<LazyLoadComponent component={PlanRegister} />}
                                    />

                                    <Route
                                        path={`${appRoutes.MASTER_MANAGEMENT.STORE_ACCESS.LIST}`}
                                        element={<LazyLoadComponent component={ListStoreAccess} />}
                                    />
                                    <Route
                                        path={`${appRoutes.MASTER_MANAGEMENT.STORE_ACCESS.REGISTER}`}
                                        element={<LazyLoadComponent component={StoreAccessRegister} />}
                                    />
                                    <Route
                                        path={`${appRoutes.MASTER_MANAGEMENT.STORE_ACCESS.UPDATE}/:id`}
                                        element={<LazyLoadComponent component={StoreAccessRegister} />}
                                    />
                                    <Route
                                        path={`${appRoutes.MASTER_MANAGEMENT.CARD.ROOT}`}
                                        element={<LazyLoadComponent component={ListCard} />}
                                    />
                                    <Route
                                        path={`${appRoutes.MASTER_MANAGEMENT.CARD.REGISTER}`}
                                        element={<LazyLoadComponent component={RegisterCard} />}
                                    />
                                    <Route
                                        path={`${appRoutes.MASTER_MANAGEMENT.CARD.EDIT}`}
                                        element={<LazyLoadComponent component={RegisterCard} />}
                                    />
                                    <Route
                                        path={`${appRoutes.MASTER_MANAGEMENT.SETTING_STORE.ROOT}`}
                                        element={<LazyLoadComponent component={SettingStore} />}
                                    />
                                    <Route
                                        path={`${appRoutes.MASTER_MANAGEMENT.SETTING_STORE.PRINT_SETTING}`}
                                        element={<LazyLoadComponent component={PrintSetting} />}
                                    />
                                    <Route
                                        path={`${appRoutes.MASTER_MANAGEMENT.PRINTER.LIST}`}
                                        element={<LazyLoadComponent component={ListPrinter} />}
                                    />
                                    <Route
                                        path={`${appRoutes.MASTER_MANAGEMENT.PRINTER.REGISTER_PRINTER}/:storeId`}
                                        element={<LazyLoadComponent component={RegisterPrinter} />}
                                    />
                                    <Route
                                        path={`${appRoutes.MASTER_MANAGEMENT.SETTING_STORE.AUTHENTICATE_SETTING}`}
                                        element={<LazyLoadComponent component={AuthenticateSetting} />}
                                    />
                                    <Route
                                        path={`${appRoutes.PRICE_SEAT_MANAGEMENT.FLOOR.LIST_FLOOR}`}
                                        element={<LazyLoadComponent component={ListFloor} />}
                                    />
                                    <Route
                                        path={`${appRoutes.PRICE_SEAT_MANAGEMENT.FLOOR.ADD_FLOOR}`}
                                        element={<LazyLoadComponent component={AddFloor} />}
                                    />
                                    <Route
                                        path={`${appRoutes.PRICE_SEAT_MANAGEMENT.FLOOR.UPDATE_FLOOR}/:id`}
                                        element={<LazyLoadComponent component={AddFloor} />}
                                    />
                                    <Route
                                        path={`${appRoutes.MASTER_MANAGEMENT.USER.LIST}`}
                                        element={<LazyLoadComponent component={ListUser} />}
                                    />
                                    <Route
                                        path={`${appRoutes.MASTER_MANAGEMENT.USER.REGISTER}`}
                                        element={<LazyLoadComponent component={AddEditUser} />}
                                    />
                                    <Route
                                        path={`${appRoutes.MASTER_MANAGEMENT.USER.REGISTER}/:id`}
                                        element={<LazyLoadComponent component={AddEditUser} />}
                                    />
                                    <Route
                                        path={`${appRoutes.MASTER_MANAGEMENT.REGISTER_RECEIPT}`}
                                        element={<LazyLoadComponent component={RegisterReceipt} />}
                                    />

                                    <Route
                                        path={`${appRoutes.PRICE_SEAT_MANAGEMENT.HISTORY_MANAGEMENT.EDIT_HISTORY_BY_MONTH}`}
                                        element={<LazyLoadComponent component={EditHistoryByMonth} />}
                                    />
                                    <Route
                                        path={`${appRoutes.PRICE_SEAT_MANAGEMENT.HISTORY_MANAGEMENT.SALES_EDIT_HISTORY}`}
                                        element={<LazyLoadComponent component={SalesEditHistory} />}
                                    />
                                    <Route
                                        path={`${appRoutes.PRICE_SEAT_MANAGEMENT.HISTORY_MANAGEMENT.SLIP_EDIT_HISTORY}`}
                                        element={<LazyLoadComponent component={SlipEditHistory} />}
                                    />

                                    <Route
                                        path={`${appRoutes.PRICE_SEAT_MANAGEMENT.HISTORY_MANAGEMENT.SLIP_EDIT_HISTORY_DETAIL}/:entryNo/:storeId`}
                                        element={<LazyLoadComponent component={SlipEditHistoryDetail} />}
                                    />

                                    <Route
                                        path={`${appRoutes.PRICE_SEAT_MANAGEMENT.HISTORY_MANAGEMENT.PRICE_EDIT_HISTORY}`}
                                        element={<LazyLoadComponent component={PriceEditHistory} />}
                                    />

                                    <Route
                                        path={`${appRoutes.PRICE_SEAT_MANAGEMENT.HISTORY_MANAGEMENT.CURRENT_EXCHANGE}`}
                                        element={<LazyLoadComponent component={CurrentExchange} />}
                                    />
                                    <Route
                                        path={`${appRoutes.PRICE_SEAT_MANAGEMENT.HISTORY_MANAGEMENT.STOCK_HISTORY}`}
                                        element={<LazyLoadComponent component={StockHistory} />}
                                    />

                                    <Route
                                        path={`${appRoutes.PRICE_SEAT_MANAGEMENT.DEVICE.EQUIPMENT.ROOT}`}
                                        element={<LazyLoadComponent component={EquipmentRegister} />}
                                    />
                                    <Route
                                        path={`${appRoutes.PRICE_SEAT_MANAGEMENT.DEVICE.EQUIPMENT.REGISTER}`}
                                        element={<LazyLoadComponent component={EquipmentRegister} />}
                                    />
                                    <Route
                                        path={`${appRoutes.PRICE_SEAT_MANAGEMENT.DEVICE.BUSINESS_DAY.REGISTER}`}
                                        element={<LazyLoadComponent component={BusinessDayRegister} />}
                                    />
                                    <Route
                                        path={`${appRoutes.PROFILE.ROOT}`}
                                        element={<LazyLoadComponent component={Profile} />}
                                    />
                                    <Route
                                        path={`${appRoutes.PROFILE.EDIT}`}
                                        element={<LazyLoadComponent component={ProfileEdit} />}
                                    />
                                    <Route
                                        path={`${appRoutes.WORK_MANAGE.LIST}`}
                                        element={<LazyLoadComponent component={ListWorkingSession} />}
                                    />
                                    <Route
                                        path={`${appRoutes.WORK_MANAGE.ADD}`}
                                        element={<LazyLoadComponent component={AddWorkingSession} />}
                                    />
                                    <Route
                                        path={`${appRoutes.WORK_MANAGE.UPDATE}/:id`}
                                        element={<LazyLoadComponent component={UpdateWorkingSession} />}
                                    />
                                    <Route
                                        path={`${appRoutes.WORK_MANAGE.REVIEW}`}
                                        element={<LazyLoadComponent component={WorkManageReview} />}
                                    />
                                    <Route
                                        path={`${appRoutes.REMOTE_MANAGEMENT.SETTING}`}
                                        element={<LazyLoadComponent component={RemoteSetting} />}
                                    />
                                    <Route
                                        path={`${appRoutes.REMOTE_MANAGEMENT.DASHBOARD}`}
                                        element={<LazyLoadComponent component={RemoteDashboard} />}
                                    />
                                    <Route
                                        path={`${appRoutes.APP_MANAGEMENT.COUPON.NORMAL}`}
                                        element={<LazyLoadComponent component={Coupon} />}
                                    />
                                    <Route
                                        path={`${appRoutes.APP_MANAGEMENT.COUPON.INSTALL}`}
                                        element={<LazyLoadComponent component={Coupon} />}
                                    />
                                    <Route
                                        path={`${appRoutes.APP_MANAGEMENT.COUPON.RANK}`}
                                        element={<LazyLoadComponent component={Coupon} />}
                                    />
                                    <Route
                                        path={`${appRoutes.APP_MANAGEMENT.COUPON.HISTORY}`}
                                        element={<LazyLoadComponent component={Coupon} />}
                                    />
                                    <Route
                                        path={`${appRoutes.APP_MANAGEMENT.MEMBER_SEARCH}`}
                                        element={<LazyLoadComponent component={CustomerMobile} />}
                                    />
                                    <Route
                                        path={`${appRoutes.APP_MANAGEMENT.APP_HISTORY.ROOT}`}
                                        element={<LazyLoadComponent component={AppHistory} />}
                                    />
                                    <Route
                                        path={`${appRoutes.APP_MANAGEMENT.TICKET.ROOT}`}
                                        element={<LazyLoadComponent component={ListTicket} />}
                                    />
                                    <Route
                                        path={`${appRoutes.APP_MANAGEMENT.TICKET.ADD}`}
                                        element={<LazyLoadComponent component={AddTicket} />}
                                    />
                                    <Route
                                        path={`${appRoutes.APP_MANAGEMENT.TICKET.UPDATE}/:id`}
                                        element={<LazyLoadComponent component={AddTicket} />}
                                    />
                                    <Route
                                        path={`${appRoutes.APP_MANAGEMENT.ISSUED_TICKET}/:id`}
                                        element={<LazyLoadComponent component={IssueTicket} />}
                                    />
                                    <Route
                                        path={`${appRoutes.APP_MANAGEMENT.ISSUED_TICKET}`}
                                        element={<LazyLoadComponent component={ListIssuedTicket} />}
                                    />
                                    <Route
                                        path={`${appRoutes.APP_MANAGEMENT.USE_TICKET.HISTORY}`}
                                        element={<LazyLoadComponent component={UsedTicketHistory} />}
                                    />
                                    <Route
                                        path={`${appRoutes.APP_MANAGEMENT.USE_TICKET.USE}`}
                                        element={<LazyLoadComponent component={UseTicket} />}
                                    />
                                    <Route
                                        path={`${appRoutes.APP_MANAGEMENT.MESSAGE}`}
                                        element={<LazyLoadComponent component={MessagePage} />}
                                    />
                                    <Route
                                        path={`${appRoutes.APP_MANAGEMENT.SETTING_BANNER}`}
                                        element={<LazyLoadComponent component={SettingBanner} />}
                                    />
                                    <Route
                                        path={`${appRoutes.APP_MANAGEMENT.HISTORY_ISSUED_STAMPS}`}
                                        element={<LazyLoadComponent component={HistoryIssuedStamps} />}
                                    />
                                    <Route
                                        path={`${appRoutes.MASTER_MANAGEMENT.OUT_TIME_BUNDLE}`}
                                        element={<LazyLoadComponent component={OutTimeBundle} />}
                                    />
                                    <Route
                                        path={`${appRoutes.APP_MANAGEMENT.APP_DOWNLOADS}`}
                                        element={<LazyLoadComponent component={AppDownloads} />}
                                    />
                                </Route>
                            </Route>
                        </Route>
                        <Route path="/500" element={<LazyLoadComponent component={ServerError} />} />
                        <Route path="*" element={<LazyLoadComponent component={NotFound} />} />
                    </Routes>
                </PageProvider>
            </BrowserRouter>
        </ConfigProvider>
    );
}

export default App;
